// TaskDetailsPage.jsx - UPDATED COMPLETE VERSION

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import moment from "moment";
import {
  getTaskById,
  updateTask,
  getAllTaskStatus,
  getAllTaskTypes,
  getTaskAssignmentsByTaskId,
  startTaskWork,
  stopTaskWork,
  pauseTaskWork,
  createTaskWorkLog,
  getTaskLogsByTaskId,
  toggleCurrentAssignment,
  createSubTask,
  getSubTasksHierarchy,
  updateSubTask,
  deleteSubTask,
  addTaskRemark,
  getTaskRemarks,
  softDeleteAssignment,
  restoreAssignment,
  getDeletedAssignmentsByTask,
  closeTask, // ADD THIS
  reopenTask, // ADD THIS
  isTaskClosed, // ADD THIS
  getContactGuid,
} from "../Api/TaskMaster";
import { fetchStagesByProject } from "../Api/StageMaster";
import {
  getBuildingBlocksByOrgAndProject,
  mapTaskToBuildingBlock,
  getBuildingBlocksByTask,
  removeSpecificTaskMapping,
} from "../Api/BuildingBlockMaster";
import { ticketDropdownApi } from "../Api/TicketApiOrg";
import { getTeamById } from "../Api/TeamMaster";
import { getUsersByIds } from "../Api/Auth";
import { fetchProjectDetails } from "../Api/ViewProjects";
import { fetchCusVenCpByCusVenType } from "../Api/CusVenCpMasterApi";
import { getPartnersByProject } from "../Api/PartnerMaster";
import "./TaskDetailsPage.css";
import { MdOutlineChatBubble } from "react-icons/md";
import { MdPersonAddAlt1 } from "react-icons/md";
import { MdAccountTree } from "react-icons/md";
import { FaCalendarCheck } from "react-icons/fa";
import { RiCompassesFill } from "react-icons/ri";
import { IoMdAddCircle } from "react-icons/io";
import { IoArrowBackCircle } from "react-icons/io5";
import { getContactNames } from "../Api/TicketApiOrg";
import { MdDeleteOutline } from "react-icons/md";

const TaskDetailsPage = () => {
  // Get params from URL if used as a route, or from props if used as modal
  const { projectId: paramProjectId, taskId: paramTaskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // User info from localStorage
  const userDetails = useMemo(() => {
    const stored = localStorage.getItem("userDetails");
    return stored ? JSON.parse(stored) : null;
  }, []);
  const username = userDetails?.username;
  const orgId = userDetails?.organisationId;
  const userId = userDetails?.userId;
  const userRoles = userDetails?.roles || [];

  const [contactNamesCache, setContactNamesCache] = useState({});

  // Task Log History filters
  const [logTypeFilter, setLogTypeFilter] = useState("ALL");
  const [filteredTaskLogs, setFilteredTaskLogs] = useState([]);

  // Role constants
  const PROJECT_ADMIN_ROLE = "Project_admin";
  const PROJECT_MANAGER_ROLE = "Project_Manager";
  const PROJECT_COMMERCIAL_ROLE = "Project_Commercial";

  const hasProjectAdminRole = () => userRoles.includes(PROJECT_ADMIN_ROLE);
  const hasProjectManagerRole = () => userRoles.includes(PROJECT_MANAGER_ROLE);
  const hasProjectCommercialRole = () =>
    userRoles.includes(PROJECT_COMMERCIAL_ROLE);
  const canEditTask = () => hasProjectAdminRole() || hasProjectManagerRole();
  const canAddSubTask = () => hasProjectAdminRole() || hasProjectManagerRole();
  const canAddComment = () => true;

  // State declarations
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskData, setTaskData] = useState(null);
  const [originalTaskData, setOriginalTaskData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [projectId, setProjectId] = useState(paramProjectId);

  // Assignee options
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [loadingAssignedTo, setLoadingAssignedTo] = useState(false);
  const [teamMemberDetails, setTeamMemberDetails] = useState([]);
  const [clientContactPersons, setClientContactPersons] = useState([]);
  const [partnerContactPersons, setPartnerContactPersons] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Add these state variables after other state declarations
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [showDeletedAssignments, setShowDeletedAssignments] = useState(false);
  const [deletedAssignments, setDeletedAssignments] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);

  // Stages
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);

  // Building blocks
  const [buildingBlocks, setBuildingBlocks] = useState([]);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [existingBlockIds, setExistingBlockIds] = useState([]);

  // Status and Task Types
  const [statusList, setStatusList] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [taskStatusMap, setTaskStatusMap] = useState({});
  const [taskTypeMap, setTaskTypeMap] = useState({});

  // Activity feed (Remarks)
  const [remarks, setRemarks] = useState([]);
  const [remarkText, setRemarkText] = useState("");
  const [loadingRemarks, setLoadingRemarks] = useState(false);
  const [editingRemarkId, setEditingRemarkId] = useState(null);
  const [editingRemarkText, setEditingRemarkText] = useState("");

  // Add these state variables after other state declarations
  const [showClosureModal, setShowClosureModal] = useState(false);
  const [closureReason, setClosureReason] = useState("");
  const [closureType, setClosureType] = useState("");
  const [closureNotes, setClosureNotes] = useState("");
  const [isClosingTask, setIsClosingTask] = useState(false);
  const [isReopeningTask, setIsReopeningTask] = useState(false);

  // Sub-tasks
  const [subTasks, setSubTasks] = useState([]);
  const [subTaskLoading, setSubTaskLoading] = useState(false);
  const [showSubTaskModal, setShowSubTaskModal] = useState(false);
  const [editingSubTask, setEditingSubTask] = useState(null);
  const [showEditSubTaskModal, setShowEditSubTaskModal] = useState(false);
  const [parentSubTaskId, setParentSubTaskId] = useState(null);
  const [subTaskForm, setSubTaskForm] = useState({
    SubTaskName: "",
    SubTaskDescription: "",
    SubTaskAssignedTo: "",
    SubTaskStatus: "",
    SubTaskDueDate: "",
    SubTaskStartDate: "",
    SubTaskEndDate: "",
    SubTaskActualStateDate: "",
    SubTaskActualEndDate: "",
    ParentSubTaskId: null,
  });

  // Assignment logs
  const [taskAssignments, setTaskAssignments] = useState([]);
  const [taskAssignLoading, setTaskAssignLoading] = useState(false);
  const [assignTaskUserId, setAssignTaskUserId] = useState("");
  const [taskLogs, setTaskLogs] = useState([]);
  const [taskLogsLoading, setTaskLogsLoading] = useState(false);
  const [workLogUserId, setWorkLogUserId] = useState("");
  const [workValue, setWorkValue] = useState("");
  const [workUnit, setWorkUnit] = useState("HOUR");
  const [savingWorkLog, setSavingWorkLog] = useState(false);

  // Helper Functions
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0];
  };

  // Add this helper function near other helper functions
  const getContactGuidFromUserId = useCallback(
    async (userId) => {
      if (!userId || !orgId) return userId;
      try {
        const response = await getContactGuid(orgId, userId);
        return response.contactGuid || userId;
      } catch (error) {
        console.error("Error getting contact GUID:", error);
        return userId; // fallback to original user ID
      }
    },
    [orgId],
  );

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    return moment(dateString).format("MMM DD, YYYY HH:mm");
  };

  const fmtHours = (h) => (h == null ? "-" : Number(h).toFixed(2));

  const getTaskStatusName = (statusGuid) => {
    if (!statusGuid) return "No Status";
    const status = statusList.find((s) => s.statusGuid === statusGuid);
    return status ? status.statusName : statusGuid;
  };

  const getTaskTypeName = (typeGuid) => {
    if (!typeGuid) return "No Type";
    const taskType = taskTypes.find((t) => t.typeGuid === typeGuid);
    return taskType ? taskType.typeName : typeGuid;
  };

  const getUsernameFromId = (userId) => {
    if (!userId) return "Unknown";

    // First check contact cache (for GUIDs)
    const contactName = contactNamesCache[userId?.toLowerCase()];
    if (contactName) return contactName;

    // Check if it's a numeric user ID (team member)
    if (/^\d+$/.test(String(userId))) {
      const userDetail = teamMemberDetails.find(
        (user) => String(user.userId) === String(userId),
      );
      if (userDetail) return userDetail.username;
    }

    // Check client contacts
    const clientContact = clientContactPersons.find(
      (c) => c.cusVenId === userId,
    );
    if (clientContact) return clientContact.contactPerName;

    // Check partner contacts
    const partnerContact = partnerContactPersons.find(
      (c) => c.cusVenId === userId,
    );
    if (partnerContact) return partnerContact.contactPerName;

    // Return shortened GUID if nothing found
    return userId.substring(0, 8) + "...";
  };

  // const getUsernameFromId = (userId) => {
  //   if (!userId) return "Unknown";

  //   // ✅ NEW: check contact cache first
  //   const contactName = contactNamesCache[userId?.toLowerCase()];
  //   if (contactName) return contactName;

  //   // existing logic
  //   const userDetail = teamMemberDetails.find(
  //     (user) => String(user.userId) === String(userId),
  //   );
  //   if (userDetail) return userDetail.username;

  //   const clientContact = clientContactPersons.find(
  //     (c) => c.cusVenId === userId,
  //   );
  //   if (clientContact) return clientContact.contactPerName;

  //   const partnerContact = partnerContactPersons.find(
  //     (c) => c.cusVenId === userId,
  //   );
  //   if (partnerContact) return partnerContact.contactPerName;

  //   // fallback
  //   return userId.substring(0, 8); // better than full GUID
  // };

  const getStageName = (stageId) => {
    if (!stageId) return "No Stage";
    const stage = stages.find((s) => s.stageId === stageId);
    return stage ? stage.stageName : stageId;
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "priority-critical";
      case "high":
        return "priority-high";
      case "medium":
        return "priority-medium";
      case "low":
        return "priority-low";
      default:
        return "priority-default";
    }
  };

  const fetchContactNames = useCallback(async (guids) => {
    if (!guids || guids.length === 0) return;

    // Filter valid GUIDs
    const validGuids = guids.filter(
      (g) =>
        g &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          g,
        ),
    );

    if (validGuids.length === 0) return;

    try {
      const res = await getContactNames(validGuids);
      console.log("Contact names response:", res);

      const map = {};
      if (Array.isArray(res)) {
        res.forEach((c) => {
          if (c.contactGuid && c.contactName) {
            map[c.contactGuid.toLowerCase()] = c.contactName;
          }
        });
      }

      setContactNamesCache((prev) => ({
        ...prev,
        ...map,
      }));
    } catch (err) {
      console.error("❌ Failed to fetch contact names", err);
    }
  }, []);

  const getStatusClass = (statusGuid) => {
    const statusName = getTaskStatusName(statusGuid).toLowerCase();
    if (statusName.includes("progress") || statusName.includes("in progress"))
      return "status-progress";
    if (statusName.includes("complete") || statusName.includes("done"))
      return "status-complete";
    if (statusName.includes("block") || statusName.includes("hold"))
      return "status-blocked";
    if (statusName.includes("not started") || statusName.includes("pending"))
      return "status-pending";
    return "status-default";
  };

  const getAssignmentStatusClass = (assignment) => {
    if (assignment.isPaused) return "status-paused";
    if (assignment.isActive) return "status-active";
    if (!assignment.startTime && !assignment.endTime)
      return "status-not-started";
    return "status-completed";
  };

  const getAllAssigneeOptions = () => {
    const options = [];
    const seen = new Set();

    teamMemberDetails.forEach((user) => {
      if (!seen.has(user.userId)) {
        seen.add(user.userId);
        options.push({
          value: user.userId,
          label: `${user.username} (Team)`,
          type: "team",
          contactName: user.username,
        });
      }
    });

    clientContactPersons.forEach((contact) => {
      if (!seen.has(contact.cusVenId)) {
        seen.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} (Client Contact)`,
          type: "client",
          contactName: contact.contactPerName,
        });
      }
    });

    partnerContactPersons.forEach((contact) => {
      if (!seen.has(contact.cusVenId)) {
        seen.add(contact.cusVenId);
        options.push({
          value: contact.cusVenId,
          label: `${contact.contactPerName} (Partner Contact)`,
          type: "partner",
          contactName: contact.contactPerName,
        });
      }
    });

    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  };

  const isNotStarted = (a) => !a.startTime && !a.endTime;
  const isActive = (a) => a.isActive === true && !!a.startTime;
  const isPaused = (a) => a.isPaused === true;

  // API Calls
  const loadAssignedToOptions = useCallback(
    async (projectId) => {
      if (!orgId || !projectId) {
        setAssignedToOptions([]);
        return;
      }

      setLoadingAssignedTo(true);
      try {
        const [team, customers, partners] = await Promise.all([
          ticketDropdownApi.getTeamAssigned(orgId, projectId),
          ticketDropdownApi.getCustomer(orgId, projectId),
          ticketDropdownApi.getPartner(orgId, projectId),
        ]);

        const assigneeMap = new Map();

        team.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Team)`,
              type: "Team",
              contactName: item.contactName,
            });
          }
        });

        customers.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Customer)`,
              type: "Customer",
              contactName: item.contactName,
            });
          }
        });

        partners.forEach((item) => {
          if (!assigneeMap.has(item.contactGUID)) {
            assigneeMap.set(item.contactGUID, {
              value: item.contactGUID,
              label: `${item.contactName} (Partner)`,
              type: "Partner",
              contactName: item.contactName,
            });
          }
        });

        const sortedOptions = Array.from(assigneeMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label),
        );

        setAssignedToOptions(sortedOptions);
      } catch (error) {
        console.error("Error loading assignee options:", error);
        toast.error("Failed to load assignee options");
        setAssignedToOptions([]);
      } finally {
        setLoadingAssignedTo(false);
      }
    },
    [orgId],
  );

  const loadTaskStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const data = await getAllTaskStatus();
      const sorted = data.sort((a, b) =>
        a.statusName.localeCompare(b.statusName),
      );
      setStatusList(sorted);
    } catch (error) {
      console.error("Error loading status:", error);
      toast.error("Failed to load status options");
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadTaskTypes = useCallback(async () => {
    try {
      setLoadingTaskTypes(true);
      const data = await getAllTaskTypes();
      const sorted = data.sort((a, b) => a.typeName.localeCompare(b.typeName));
      setTaskTypes(sorted);
    } catch (error) {
      console.error("Error loading task types:", error);
      toast.error("Failed to load task types");
    } finally {
      setLoadingTaskTypes(false);
    }
  }, []);

  const fetchProjectStages = useCallback(async (projectId) => {
    if (!projectId) return;
    try {
      setLoadingStages(true);
      const stageData = await fetchStagesByProject(projectId);
      if (Array.isArray(stageData)) {
        setStages(stageData);
      } else {
        setStages([]);
      }
    } catch (error) {
      console.error("Error fetching stages:", error);
      setStages([]);
    } finally {
      setLoadingStages(false);
    }
  }, []);

  const fetchTeamMembers = async (projectId) => {
    if (!projectId) return;
    try {
      const projectData = await fetchProjectDetails(projectId);
      if (projectData && projectData.teamAssigned) {
        const teamData = await getTeamById(projectData.teamAssigned);
        if (teamData && teamData.teamMembers) {
          const numericUserIds = teamData.teamMembers
            .map((id) => parseInt(id))
            .filter((id) => !isNaN(id));
          if (numericUserIds.length > 0) {
            const response = await getUsersByIds(numericUserIds);
            if (response && response.users) {
              setTeamMemberDetails(response.users);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching team members:", error);
    }
  };

  const fetchClientContactPersons = async (clientId) => {
    if (!clientId) return;
    try {
      const contacts = await fetchCusVenCpByCusVenType(clientId);
      if (Array.isArray(contacts)) {
        setClientContactPersons(contacts);
      }
    } catch (error) {
      console.error("Error fetching client contacts:", error);
    }
  };

  const fetchPartnerContactPersons = async (projectId) => {
    try {
      const partners = await getPartnersByProject(projectId);
      if (partners && partners.length > 0) {
        const allContacts = [];
        for (const partner of partners) {
          const contacts = await fetchCusVenCpByCusVenType(partner.partnerName);
          if (Array.isArray(contacts)) {
            allContacts.push(...contacts);
          }
        }
        setPartnerContactPersons(allContacts);
      }
    } catch (error) {
      console.error("Error fetching partner contacts:", error);
    }
  };

  const loadBuildingBlocks = useCallback(
    async (projectId, currentTaskId) => {
      if (!orgId || !projectId) return;
      try {
        setLoadingBlocks(true);
        const blocks = await getBuildingBlocksByOrgAndProject(orgId, projectId);
        setBuildingBlocks(blocks || []);
        const mappedBlocks = await getBuildingBlocksByTask(currentTaskId);
        const mappedIds = mappedBlocks || [];
        setExistingBlockIds(mappedIds);
        setSelectedBlockIds(mappedIds);
      } catch (error) {
        console.error("Failed to load building blocks:", error);
        toast.error("Failed to load building blocks");
      } finally {
        setLoadingBlocks(false);
      }
    },
    [orgId],
  );

  const loadTaskAssignments = async (taskId) => {
    try {
      setTaskAssignLoading(true);
      const data = await getTaskAssignmentsByTaskId(taskId);
      setTaskAssignments(data || []);
    } catch (error) {
      console.error("Error loading task assignments:", error);
      setTaskAssignments([]);
    } finally {
      setTaskAssignLoading(false);
    }
  };

  const loadTaskLogs = async (taskId) => {
    try {
      setTaskLogsLoading(true);
      const logs = await getTaskLogsByTaskId(taskId);
      setTaskLogs(logs || []);
    } catch (error) {
      console.error("Error loading task logs:", error);
      setTaskLogs([]);
    } finally {
      setTaskLogsLoading(false);
    }
  };

  const loadRemarks = async (taskId) => {
    try {
      setLoadingRemarks(true);
      const data = await getTaskRemarks(taskId);
      setRemarks(data || []);
    } catch (error) {
      console.error("Error loading remarks:", error);
      setRemarks([]);
    } finally {
      setLoadingRemarks(false);
    }
  };

  const loadSubTasks = async (taskId) => {
    try {
      const data = await getSubTasksHierarchy(taskId);
      setSubTasks(data || []);
    } catch (error) {
      console.error("Error loading subtasks:", error);
      setSubTasks([]);
    }
  };

  const loadTaskDetails = useCallback(async () => {
    if (!paramTaskId) return;

    setLoading(true);
    try {
      const taskDetails = await getTaskById(paramTaskId);
      setTaskData(taskDetails);
      setOriginalTaskData(taskDetails);
      setProjectId(taskDetails.projectBelong);

      if (taskDetails.projectBelong) {
        await Promise.all([
          loadAssignedToOptions(taskDetails.projectBelong),
          fetchProjectStages(taskDetails.projectBelong),
          loadBuildingBlocks(taskDetails.projectBelong, paramTaskId),
          fetchTeamMembers(taskDetails.projectBelong),
          fetchPartnerContactPersons(taskDetails.projectBelong),
        ]);

        // Fetch client contacts if project has client
        const projectData = await fetchProjectDetails(
          taskDetails.projectBelong,
        );
        if (projectData && projectData.prClient) {
          await fetchClientContactPersons(projectData.prClient);
        }

        // Load assignments, logs, remarks, subtasks
        await Promise.all([
          loadTaskAssignments(paramTaskId),
          loadTaskLogs(paramTaskId),
          loadRemarks(paramTaskId),
          loadSubTasks(paramTaskId),
        ]);
      }
    } catch (error) {
      console.error("Error loading task details:", error);
      toast.error("Failed to load task details");
      handleBack();
    } finally {
      setLoading(false);
    }
  }, [
    paramTaskId,
    loadAssignedToOptions,
    fetchProjectStages,
    loadBuildingBlocks,
  ]);

  // Check if task is closed on load
  const [taskClosedStatus, setTaskClosedStatus] = useState({
    isClosed: false,
    status: "Open",
  });

  const checkTaskClosedStatus = useCallback(async () => {
    if (!paramTaskId) return;
    try {
      const status = await isTaskClosed(paramTaskId);
      setTaskClosedStatus(status);
    } catch (error) {
      console.error("Error checking task closure status:", error);
    }
  }, [paramTaskId]);

  // Call this in your loadTaskDetails function or in a useEffect
  useEffect(() => {
    if (paramTaskId) {
      checkTaskClosedStatus();
    }
  }, [paramTaskId, checkTaskClosedStatus]);

  // Handle back navigation
  // const handleBack = () => {
  //   if (projectId) {
  //     const searchParams = new URLSearchParams(location.search);
  //     const activeTab = searchParams.get("tab") || "tasks";
  //     navigate(`/project/${projectId}?tab=${activeTab}`);
  //   } else {
  //     navigate(-1);
  //   }
  // };

  // In TaskDetailsPage.jsx, update the handleBack function:

  // Handle back navigation
  // Complete updated handleBack function for TaskDetailsPage.jsx:

  // Update the handleBack function to use sessionStorage as fallback
  const handleBack = () => {
    if (projectId) {
      // Try to get tab from URL query param first
      const searchParams = new URLSearchParams(location.search);
      let activeTab = searchParams.get("tab");

      // If not in URL, try to get from location state (passed from ProjectView)
      if (!activeTab && location.state?.activeTab) {
        activeTab = location.state.activeTab;
      }

      // If still not found, try sessionStorage
      if (!activeTab) {
        const storedTab = sessionStorage.getItem(
          `task_details_back_tab_${projectId}`,
        );
        if (storedTab) {
          activeTab = storedTab;
          // Clear it after use
          sessionStorage.removeItem(`task_details_back_tab_${projectId}`);
        }
      }

      // Default to "tasks" if still no tab
      if (!activeTab) {
        activeTab = "tasks";
      }

      // Navigate back to project with the correct tab
      navigate(`/project/${projectId}?tab=${activeTab}`);
    } else {
      navigate(-1);
    }
  };

  // Initial load
  useEffect(() => {
    if (paramTaskId) {
      loadTaskDetails();
      loadTaskStatus();
      loadTaskTypes();
    }
  }, [paramTaskId, loadTaskDetails, loadTaskStatus, loadTaskTypes]);

  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (!isEditing) setIsEditing(true);
    setTaskData((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    if (!isEditing) setIsEditing(true);
    setTaskData((prev) => (prev ? { ...prev, [name]: value || null } : null));
  };

  const handleAssigneeChange = (e) => {
    if (!isEditing) setIsEditing(true);
    setTaskData((prev) =>
      prev ? { ...prev, assignedTo: e.target.value } : null,
    );
  };

  const handleStageChange = (e) => {
    if (!isEditing) setIsEditing(true);
    setTaskData((prev) =>
      prev ? { ...prev, stageBelong: e.target.value || null } : null,
    );
  };

  const handleTagsChange = (e) => {
    if (!isEditing) setIsEditing(true);
    setTaskData((prev) =>
      prev ? { ...prev, taskTags: e.target.value } : null,
    );
  };

  // Building block selection
  const buildingBlockOptions = useMemo(() => {
    return buildingBlocks.map((block) => ({
      value: block.buildingBlockId,
      label: `${block.buildingBlockName} (${block.buildingBlockId})`,
    }));
  }, [buildingBlocks]);

  const handleBuildingBlockChange = (selectedOptions) => {
    if (!isEditing) setIsEditing(true);
    const values = selectedOptions
      ? selectedOptions.map((opt) => opt.value)
      : [];
    setSelectedBlockIds(values);
  };

  // Submit handler
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   if (!taskData) return;

  //   setSaving(true);
  //   try {
  //     const updateData = {
  //       TaskName: taskData.taskName,
  //       ProjectBelong: taskData.projectBelong,
  //       AssignedTo: taskData.assignedTo,
  //       TaskStartDate: taskData.taskStartDate,
  //       TaskEndDate: taskData.taskEndDate,
  //       TaskType: taskData.taskType,
  //       TaskPriority: taskData.taskPriority,
  //       TaskStatus: taskData.taskStatus,
  //       TaskDescription: taskData.taskDescription,
  //       ActualStartDate: taskData.actualStartDate,
  //       ActualEndDate: taskData.actualEndDate,
  //       TaskTags: taskData.taskTags,
  //       EditedBy: username,
  //       StageBelong: taskData.stageBelong || null,
  //     };

  //     await updateTask(paramTaskId, updateData);

  //     // Handle building block mappings
  //     const blocksToAdd = selectedBlockIds.filter(
  //       (id) => !existingBlockIds.includes(id),
  //     );
  //     const blocksToRemove = existingBlockIds.filter(
  //       (id) => !selectedBlockIds.includes(id),
  //     );

  //     for (const blockId of blocksToAdd) {
  //       await mapTaskToBuildingBlock({
  //         buildingBlockId: blockId,
  //         taskId: paramTaskId,
  //         orgId: String(orgId),
  //         createdBy: String(userId),
  //       });
  //     }

  //     for (const blockId of blocksToRemove) {
  //       await removeSpecificTaskMapping(paramTaskId, blockId);
  //     }

  //     setExistingBlockIds([...selectedBlockIds]);
  //     setOriginalTaskData(taskData);
  //     setIsEditing(false);
  //     toast.success("Task updated successfully");
  //   } catch (error) {
  //     console.error("Error updating task:", error);
  //     toast.error("Failed to update task");
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  // Submit handler - with closure logic
  // Submit handler - with closure logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!taskData) return;

    // Get the status names for comparison
    const originalStatusName = getTaskStatusName(originalTaskData?.taskStatus);
    const newStatusName = getTaskStatusName(taskData.taskStatus);

    const isClosing =
      (newStatusName === "Completed" || newStatusName === "Cancelled") &&
      originalStatusName !== newStatusName;

    const isReopening =
      taskClosedStatus.isClosed &&
      newStatusName !== "Completed" &&
      newStatusName !== "Cancelled";

    // If closing, show closure modal instead of direct update
    if (isClosing && !isReopening) {
      setClosureType(newStatusName === "Completed" ? "Completed" : "Cancelled");
      setShowClosureModal(true);
      return;
    }

    // If reopening from closed state
    if (isReopening) {
      setShowReopenModal(true);
      return;
    }

    // Normal update without closure
    await performTaskUpdate();
  };

  // New function to perform the actual update
  // New function to perform the actual update
  const performTaskUpdate = async () => {
    setSaving(true);
    try {
      // Get contact GUID for the editedBy user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      const updateData = {
        TaskName: taskData.taskName,
        ProjectBelong: taskData.projectBelong,
        AssignedTo: taskData.assignedTo,
        TaskStartDate: taskData.taskStartDate,
        TaskEndDate: taskData.taskEndDate,
        TaskType: taskData.taskType,
        TaskPriority: taskData.taskPriority,
        TaskStatus: taskData.taskStatus,
        TaskDescription: taskData.taskDescription,
        ActualStartDate: taskData.actualStartDate,
        ActualEndDate: taskData.actualEndDate,
        TaskTags: taskData.taskTags,
        EditedBy: contactGuid, // Use contact GUID instead of username
        StageBelong: taskData.stageBelong || null,
      };

      await updateTask(paramTaskId, updateData);

      // Handle building block mappings
      const blocksToAdd = selectedBlockIds.filter(
        (id) => !existingBlockIds.includes(id),
      );
      const blocksToRemove = existingBlockIds.filter(
        (id) => !selectedBlockIds.includes(id),
      );

      for (const blockId of blocksToAdd) {
        await mapTaskToBuildingBlock({
          buildingBlockId: blockId,
          taskId: paramTaskId,
          orgId: String(orgId),
          createdBy: contactGuid, // Use contact GUID
        });
      }

      for (const blockId of blocksToRemove) {
        await removeSpecificTaskMapping(paramTaskId, blockId);
      }

      setExistingBlockIds([...selectedBlockIds]);
      setOriginalTaskData(taskData);
      setIsEditing(false);
      toast.success("Task updated successfully");

      // Refresh task closure status
      await checkTaskClosedStatus();
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setSaving(false);
    }
  };

  // Handle task closure

  // Handle task closure
  // Handle task closure
  const handleCloseTask = async () => {
    if (!closureReason.trim()) {
      toast.error("Please provide a reason for closing the task");
      return;
    }

    setIsClosingTask(true);
    try {
      // Get contact GUID for the closedBy user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      // Get the status GUID for the closure type from the selected status
      const statusGuid = taskData.taskStatus;

      const closureData = {
        taskId: paramTaskId,
        closedBy: contactGuid, // Use contact GUID instead of user ID
        closureReason: closureReason,
        closureType: closureType,
        taskStatus: statusGuid,
        additionalNotes: closureNotes || null,
        finalWorkValue: null,
        workUnit: "HOUR",
      };

      console.log("Sending closure data with contact GUID:", closureData);

      const result = await closeTask(closureData);

      if (result.success) {
        toast.success(`Task closed successfully as ${closureType}`);
        setShowClosureModal(false);
        setClosureReason("");
        setClosureNotes("");
        setClosureType("");

        // Refresh task data and closure status
        await loadTaskDetails();
        await checkTaskClosedStatus();
        setIsEditing(false);
      } else {
        toast.error(result.message || "Failed to close task");
      }
    } catch (error) {
      console.error("Error closing task:", error);
      toast.error(error.message || "Failed to close task");
    } finally {
      setIsClosingTask(false);
    }
  };

  // Handle task closure
  // const handleCloseTask = async () => {
  //   if (!closureReason.trim()) {
  //     toast.error("Please provide a reason for closing the task");
  //     return;
  //   }

  //   setIsClosingTask(true);
  //   try {
  //     // Find the status GUID for the closure type
  //     let statusGuid = taskData.taskStatus;

  //     // If taskStatus is not set, try to find it from statusList
  //     if (!statusGuid || statusGuid === "") {
  //       const matchingStatus = statusList.find(
  //         (s) => s.statusName.toLowerCase() === closureType.toLowerCase(),
  //       );
  //       if (matchingStatus) {
  //         statusGuid = matchingStatus.statusGuid;
  //       }
  //     }

  //     const closureData = {
  //       taskId: paramTaskId,
  //       closedBy: String(userId),
  //       closureReason: closureReason,
  //       closureType: closureType,
  //       taskStatus: statusGuid, // Send the status GUID
  //       additionalNotes: closureNotes || null,
  //       finalWorkValue: null,
  //       workUnit: "HOUR",
  //     };

  //     console.log("Sending closure data:", closureData); // Debug log

  //     const result = await closeTask(closureData);

  //     console.log("Closure response:", result); // Debug log

  //     if (result.success) {
  //       toast.success(`Task closed successfully as ${closureType}`);
  //       setShowClosureModal(false);
  //       setClosureReason("");
  //       setClosureNotes("");
  //       setClosureType("");

  //       // Refresh task data and closure status
  //       await loadTaskDetails();
  //       await checkTaskClosedStatus();
  //       setIsEditing(false);

  //       // Also refresh task data to get updated status
  //       const updatedTask = await getTaskById(paramTaskId);
  //       setTaskData(updatedTask);
  //       setOriginalTaskData(updatedTask);
  //     } else {
  //       toast.error(result.message || "Failed to close task");
  //     }
  //   } catch (error) {
  //     console.error("Error closing task:", error);
  //     toast.error(error.message || "Failed to close task");
  //   } finally {
  //     setIsClosingTask(false);
  //   }
  // };

  // Reopen modal state
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenNotes, setReopenNotes] = useState("");

  // Handle task reopening
  // Handle task reopening
  const handleReopenTask = async () => {
    if (!reopenReason.trim()) {
      toast.error("Please provide a reason for reopening the task");
      return;
    }

    setIsReopeningTask(true);
    try {
      // Get contact GUID for the reopenedBy user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      // Find an appropriate status for reopened task
      let statusGuid = taskData.taskStatus;

      // If no status, try to find "In Progress" status
      if (!statusGuid || statusGuid === "") {
        const inProgressStatus = statusList.find(
          (s) => s.statusName.toLowerCase() === "in progress",
        );
        if (inProgressStatus) {
          statusGuid = inProgressStatus.statusGuid;
        }
      }

      const reopenData = {
        taskId: paramTaskId,
        reopenedBy: contactGuid, // Use contact GUID instead of user ID
        reopenReason: reopenReason,
        taskStatus: statusGuid,
        additionalNotes: reopenNotes || null,
      };

      console.log("Sending reopen data with contact GUID:", reopenData);

      const result = await reopenTask(reopenData);

      if (result.success) {
        toast.success("Task reopened successfully");
        setShowReopenModal(false);
        setReopenReason("");
        setReopenNotes("");

        // Refresh task data and closure status
        await loadTaskDetails();
        await checkTaskClosedStatus();
        setIsEditing(false);
      } else {
        toast.error(result.message || "Failed to reopen task");
      }
    } catch (error) {
      console.error("Error reopening task:", error);
      toast.error(error.message || "Failed to reopen task");
    } finally {
      setIsReopeningTask(false);
    }
  };

  const handleDiscard = () => {
    if (originalTaskData) {
      setTaskData(originalTaskData);
      setSelectedBlockIds(existingBlockIds);
      setIsEditing(false);
    }
    toast.info("Changes discarded");
  };

  // Remark handlers
  // Remark handlers
  const handleAddRemark = async () => {
    if (!remarkText.trim()) {
      toast.warning("Please enter a remark");
      return;
    }

    try {
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await addTaskRemark({
        taskId: paramTaskId,
        remark: remarkText,
        createdBy: contactGuid, // Use contact GUID
      });
      toast.success("Remark added");
      setRemarkText("");
      await loadRemarks(paramTaskId);
    } catch (error) {
      console.error("Error adding remark:", error);
      toast.error("Failed to add remark");
    }
  };

  const startEditRemark = (remark) => {
    setEditingRemarkId(remark.taskLogId);
    setEditingRemarkText(remark.logDescription);
  };

  const cancelEditRemark = () => {
    setEditingRemarkId(null);
    setEditingRemarkText("");
  };

  useEffect(() => {
    // Store the activeTab from location state when component mounts
    if (location.state?.activeTab) {
      sessionStorage.setItem(
        `task_details_back_tab_${projectId}`,
        location.state.activeTab,
      );
    }
  }, [location.state, projectId]);

  // Filter task logs based on selected type
  useEffect(() => {
    if (!taskLogs.length) {
      setFilteredTaskLogs([]);
      return;
    }

    if (logTypeFilter === "ALL") {
      setFilteredTaskLogs(taskLogs);
    } else {
      const filtered = taskLogs.filter((log) => log.logType === logTypeFilter);
      setFilteredTaskLogs(filtered);
    }
  }, [logTypeFilter, taskLogs]);

  // Add this useEffect to fetch contact names for task logs
  useEffect(() => {
    if (!filteredTaskLogs.length) return;

    // Collect all unique user IDs (CreatedBy, AssignedUserId, ClosedBy)
    const userIds = new Set();
    filteredTaskLogs.forEach((log) => {
      if (log.createdBy) userIds.add(log.createdBy);
      if (log.assignedUserId) userIds.add(log.assignedUserId);
      if (log.closedBy) userIds.add(log.closedBy);
    });

    if (userIds.size > 0) {
      fetchContactNames(Array.from(userIds));
    }
  }, [filteredTaskLogs, fetchContactNames]);

  const renderSubTasksTree = (tasks, level = 0) => {
    if (!tasks || tasks.length === 0) return null;

    return tasks.map((subTask, index) => (
      <div key={subTask.subTaskId} className="ntp-tree-node">
        <div
          className="ntp-tree-node-content"
          style={{ paddingLeft: `${level * 32}px` }}
        >
          {/* Tree line connector */}
          {level > 0 && (
            <div className="ntp-tree-connector">
              <div className="ntp-tree-line"></div>
            </div>
          )}

          {/* Node icon and content */}
          <div className="ntp-tree-node-main">
            <div className="ntp-tree-node-icon">
              {subTask.children && subTask.children.length > 0 ? (
                <span className="ntp-tree-folder-icon">📁</span>
              ) : (
                <span className="ntp-tree-file-icon">📄</span>
              )}
            </div>

            <div className="ntp-tree-node-info">
              <div className="ntp-tree-node-header">
                <span className="ntp-tree-node-title">
                  {subTask.subTaskName}
                </span>
                <span
                  className={`ntp-tree-node-status ${getStatusClass(subTask.subTaskStatus)}`}
                >
                  {getTaskStatusName(subTask.subTaskStatus) || "No Status"}
                </span>
              </div>

              {subTask.subTaskDescription && (
                <div className="ntp-tree-node-description">
                  {subTask.subTaskDescription}
                </div>
              )}

              {subTask.subTaskAssignedTo && (
                <div className="ntp-tree-node-meta">
                  <span className="ntp-tree-node-assignee">
                    👤 {getUsernameFromId(subTask.subTaskAssignedTo)}
                  </span>
                  {subTask.subTaskDueDate && (
                    <span className="ntp-tree-node-due">
                      📅 Due: {formatDateForDisplay(subTask.subTaskDueDate)}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="ntp-tree-node-actions">
              {canAddSubTask() && (
                <button
                  onClick={() => handleAddSubTask(subTask.subTaskId)}
                  className="ntp-tree-action-btn ntp-tree-add-btn"
                  title="Add Child"
                >
                  <IoMdAddCircle />
                </button>
              )}
              <button
                onClick={() => handleEditSubTask(subTask)}
                className="ntp-tree-action-btn ntp-tree-edit-btn"
                title="Edit"
              >
                ✏️
              </button>
              <button
                onClick={() => handleDeleteSubTask(subTask.subTaskId)}
                className="ntp-tree-action-btn ntp-tree-delete-btn"
                title="Delete"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        {/* Children nodes */}
        {subTask.children && subTask.children.length > 0 && (
          <div className="ntp-tree-children">
            {renderSubTasksTree(subTask.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  const handleUpdateRemark = async (logId, newRemark) => {
    if (!newRemark.trim()) {
      toast.error("Please enter a remark");
      return;
    }
    // Note: You'll need an updateTaskRemark API endpoint
    // For now, just show info
    toast.info("Edit remark feature - API endpoint needed");
    cancelEditRemark();
  };

  // Assignment handlers
  // Assignment handlers
  const handleAssignUserOnly = async () => {
    if (!taskData?.taskId || !assignTaskUserId) {
      toast.error("Please select a user to assign");
      return;
    }

    try {
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await updateTask(taskData.taskId, {
        assignedTo: assignTaskUserId,
        EditedBy: contactGuid, // Use contact GUID
      });
      toast.success("User assigned to task");
      await loadTaskAssignments(taskData.taskId);
      setAssignTaskUserId("");
    } catch (error) {
      console.error("Error assigning user:", error);
      toast.error("Failed to assign user");
    }
  };

  const handleStartTaskWork = async (assignedUserId) => {
    if (!taskData?.taskId) return;
    try {
      await startTaskWork(taskData.taskId, assignedUserId, orgId);
      toast.success("Work started");
      await loadTaskAssignments(taskData.taskId);
      await loadTaskLogs(taskData.taskId);
    } catch (error) {
      console.error("Error starting work:", error);
      toast.error("Failed to start work");
    }
  };

  const handlePauseTaskWork = async (assignmentId) => {
    try {
      await pauseTaskWork(assignmentId);
      toast.info("Work paused");
      await loadTaskAssignments(taskData.taskId);
    } catch (error) {
      console.error("Error pausing work:", error);
      toast.error("Failed to pause work");
    }
  };

  const handleStopTaskWork = async (assignmentId) => {
    try {
      await stopTaskWork(assignmentId);
      toast.success("Work stopped");
      await loadTaskAssignments(taskData.taskId);
      await loadTaskLogs(taskData.taskId);
    } catch (error) {
      console.error("Error stopping work:", error);
      toast.error("Failed to stop work");
    }
  };

  const handleToggleCurrentAssignment = async (assignmentId, isCurrent) => {
    if (!canEditTask()) {
      toast.error("Only Admins/Managers can change current assignment");
      return;
    }
    try {
      await toggleCurrentAssignment(assignmentId);
      toast.success(
        isCurrent
          ? "Removed from current assignment"
          : "Set as current assignment",
      );
      await loadTaskAssignments(taskData.taskId);
    } catch (error) {
      console.error("Error toggling current assignment:", error);
      toast.error("Failed to update current assignment");
    }
  };

  const handleSaveWorkLog = async () => {
    if (!taskData?.taskId) {
      toast.error("No task selected");
      return;
    }
    if (!workLogUserId || !workValue) {
      toast.error("Please select user and work value");
      return;
    }

    const selectedUser = getAllAssigneeOptions().find(
      (opt) => String(opt.value) === String(workLogUserId),
    );

    try {
      setSavingWorkLog(true);
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await createTaskWorkLog({
        taskId: taskData.taskId,
        projectId: projectId,
        orgId: String(orgId),
        assignedUserId: workLogUserId,
        workValue: parseFloat(workValue),
        workUnit,
        logType: "WORK_LOG",
        logDescription: `Manual work log: ${workValue} ${workUnit} - ${selectedUser?.type || "User"}`,
        createdBy: contactGuid, // Use contact GUID
      });
      toast.success("Work log added");
      await loadTaskLogs(taskData.taskId);
      setWorkLogUserId("");
      setWorkValue("");
      setWorkUnit("HOUR");
    } catch (error) {
      console.error("Error adding work log:", error);
      toast.error("Failed to add work log");
    } finally {
      setSavingWorkLog(false);
    }
  };

  // Sub-task handlers
  const handleAddSubTask = (parentId = null) => {
    setParentSubTaskId(parentId);
    setSubTaskForm({
      SubTaskName: "",
      SubTaskDescription: "",
      SubTaskAssignedTo: taskData?.assignedTo || "", // This might be GUID or ID
      SubTaskStatus: "",
      SubTaskDueDate: taskData?.taskEndDate
        ? moment(taskData.taskEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskStartDate: "",
      SubTaskEndDate: "",
      SubTaskActualStateDate: "",
      SubTaskActualEndDate: "",
      ParentSubTaskId: parentId,
    });
    setShowSubTaskModal(true);
  };

  const handleSubTaskFormChange = (field, value) => {
    setSubTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubTaskSubmit = async () => {
    if (!subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }

    setSubTaskLoading(true);
    try {
      // Get contact GUID for the current user (created by)
      const contactGuid = await getContactGuidFromUserId(String(userId));

      // ✅ Convert assigned user ID to Contact GUID
      let assignedToContactGuid = null;
      if (subTaskForm.SubTaskAssignedTo) {
        // If it's already a GUID format, use as is
        const isGuid =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            subTaskForm.SubTaskAssignedTo,
          );
        if (isGuid) {
          assignedToContactGuid = subTaskForm.SubTaskAssignedTo;
        } else {
          // Convert user ID to Contact GUID
          assignedToContactGuid = await getContactGuidFromUserId(
            String(subTaskForm.SubTaskAssignedTo),
          );
        }
      }

      await createSubTask(
        {
          SubTaskName: subTaskForm.SubTaskName,
          SubTaskDescription: subTaskForm.SubTaskDescription,
          SubTaskAssignedTo: assignedToContactGuid, // ✅ Send Contact GUID
          SubTaskStatus: subTaskForm.SubTaskStatus,
          SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
          SubTaskStartDate: subTaskForm.SubTaskStartDate || null,
          SubTaskEndDate: subTaskForm.SubTaskEndDate || null,
          SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
          SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
          SubTaskBelongTask: paramTaskId,
          SubTaskCreatedBy: contactGuid, // ✅ Send Contact GUID
          ParentSubTaskId: parentSubTaskId,
        },
        username,
      );
      toast.success("SubTask created successfully");
      setShowSubTaskModal(false);
      await loadSubTasks(paramTaskId);
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error("Failed to create subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  const handleEditSubTask = (subTask) => {
    console.log("Editing subtask:", subTask);
    setEditingSubTask(subTask);
    setSubTaskForm({
      SubTaskName: subTask.subTaskName || "",
      SubTaskDescription: subTask.subTaskDescription || "",
      SubTaskAssignedTo: subTask.subTaskAssignedTo || "", // This will be the stored value (could be GUID or ID)
      SubTaskStatus: subTask.subTaskStatus || "",
      SubTaskDueDate: subTask.subTaskDueDate
        ? moment(subTask.subTaskDueDate).format("YYYY-MM-DD")
        : "",
      SubTaskStartDate: subTask.subTaskStartDate
        ? moment(subTask.subTaskStartDate).format("YYYY-MM-DD")
        : "",
      SubTaskEndDate: subTask.subTaskEndDate
        ? moment(subTask.subTaskEndDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualStateDate: subTask.subTaskActualStateDate
        ? moment(subTask.subTaskActualStateDate).format("YYYY-MM-DD")
        : "",
      SubTaskActualEndDate: subTask.subTaskActualEndDate
        ? moment(subTask.subTaskActualEndDate).format("YYYY-MM-DD")
        : "",
      ParentSubTaskId: subTask.parentSubTaskId || null,
    });
    setShowEditSubTaskModal(true);
  };

  const handleUpdateSubTask = async () => {
    if (!editingSubTask || !subTaskForm.SubTaskName.trim()) {
      toast.error("SubTask name is required");
      return;
    }

    setSubTaskLoading(true);
    try {
      // Get contact GUID for the current user (edited by)
      const contactGuid = await getContactGuidFromUserId(String(userId));

      // ✅ Convert assigned user ID to Contact GUID
      let assignedToContactGuid = null;
      if (subTaskForm.SubTaskAssignedTo) {
        // If it's already a GUID format, use as is
        const isGuid =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            subTaskForm.SubTaskAssignedTo,
          );
        if (isGuid) {
          assignedToContactGuid = subTaskForm.SubTaskAssignedTo;
        } else {
          // Convert user ID to Contact GUID
          assignedToContactGuid = await getContactGuidFromUserId(
            String(subTaskForm.SubTaskAssignedTo),
          );
        }
      }

      await updateSubTask(editingSubTask.subTaskId, {
        SubTaskName: subTaskForm.SubTaskName,
        SubTaskDescription: subTaskForm.SubTaskDescription,
        SubTaskAssignedTo: assignedToContactGuid, // ✅ Send Contact GUID
        SubTaskStatus: subTaskForm.SubTaskStatus,
        SubTaskDueDate: subTaskForm.SubTaskDueDate || null,
        SubTaskStartDate: subTaskForm.SubTaskStartDate || null,
        SubTaskEndDate: subTaskForm.SubTaskEndDate || null,
        SubTaskActualStateDate: subTaskForm.SubTaskActualStateDate || null,
        SubTaskActualEndDate: subTaskForm.SubTaskActualEndDate || null,
        SubTaskEditedBy: contactGuid, // ✅ Send Contact GUID
      });

      toast.success("SubTask updated successfully");
      setShowEditSubTaskModal(false);
      setEditingSubTask(null);
      await loadSubTasks(paramTaskId);
    } catch (error) {
      console.error("Error updating subtask:", error);
      toast.error(error.message || "Failed to update subtask");
    } finally {
      setSubTaskLoading(false);
    }
  };

  // Filter task logs based on selected type
  useEffect(() => {
    if (!taskLogs.length) {
      setFilteredTaskLogs([]);
      return;
    }

    if (logTypeFilter === "ALL") {
      setFilteredTaskLogs(taskLogs);
    } else {
      const filtered = taskLogs.filter((log) => log.logType === logTypeFilter);
      setFilteredTaskLogs(filtered);
    }
  }, [logTypeFilter, taskLogs]);

  // Get unique log types for filter dropdown
  const getUniqueLogTypes = useMemo(() => {
    const types = new Set();
    types.add("ALL");
    taskLogs.forEach((log) => {
      if (log.logType) {
        types.add(log.logType);
      }
    });
    return Array.from(types);
  }, [taskLogs]);

  // ✅ Soft delete assignment handler
  const handleSoftDeleteAssignment = async (assignment) => {
    setSelectedAssignment(assignment);
    setDeleteReason("");
    setShowDeleteConfirm(true);
  };

  // Confirm soft delete
  // Confirm soft delete
  const confirmSoftDelete = async () => {
    if (!selectedAssignment) return;

    try {
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await softDeleteAssignment(
        selectedAssignment.assignmentId,
        contactGuid, // Use contact GUID
        deleteReason,
      );
      toast.success("Assignment soft deleted successfully");
      setShowDeleteConfirm(false);
      setSelectedAssignment(null);
      setDeleteReason("");

      // Refresh assignments list
      await loadTaskAssignments(paramTaskId);

      // If showing deleted assignments, refresh those too
      if (showDeletedAssignments) {
        await loadDeletedAssignments();
      }
    } catch (error) {
      console.error("Error soft deleting assignment:", error);
      toast.error(error.message || "Failed to delete assignment");
    }
  };

  // ✅ Restore assignment handler
  // ✅ Restore assignment handler
  const handleRestoreAssignment = async (assignment) => {
    if (
      !window.confirm(
        `Are you sure you want to restore assignment for ${getUsernameFromId(assignment.assignedUserId)}?`,
      )
    ) {
      return;
    }

    try {
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await restoreAssignment(
        assignment.assignmentId,
        contactGuid, // Use contact GUID
        "Restored from deleted records",
      );
      toast.success("Assignment restored successfully");

      // Refresh both lists
      await loadTaskAssignments(paramTaskId);
      await loadDeletedAssignments();
    } catch (error) {
      console.error("Error restoring assignment:", error);
      toast.error(error.message || "Failed to restore assignment");
    }
  };

  // ✅ Load deleted assignments
  const loadDeletedAssignments = async () => {
    if (!paramTaskId) return;

    setLoadingDeleted(true);
    try {
      const data = await getDeletedAssignmentsByTask(paramTaskId);
      setDeletedAssignments(data || []);
    } catch (error) {
      console.error("Error loading deleted assignments:", error);
      setDeletedAssignments([]);
    } finally {
      setLoadingDeleted(false);
    }
  };

  // ✅ Toggle deleted assignments view
  const toggleDeletedAssignments = async () => {
    if (!showDeletedAssignments) {
      await loadDeletedAssignments();
    }
    setShowDeletedAssignments(!showDeletedAssignments);
  };

  const handleDeleteSubTask = async (subTaskId) => {
    if (!window.confirm("Are you sure you want to delete this subtask?"))
      return;
    try {
      // Get contact GUID for the current user
      const contactGuid = await getContactGuidFromUserId(String(userId));

      await deleteSubTask(subTaskId);
      toast.success("SubTask deleted successfully");
      await loadSubTasks(paramTaskId);
    } catch (error) {
      console.error("Error deleting subtask:", error);
      toast.error("Failed to delete subtask");
    }
  };

  const renderSubTasks = (tasks, level = 0) => {
    if (!tasks || tasks.length === 0) return null;

    return tasks.map((subTask) => (
      <div
        key={subTask.subTaskId}
        className="ntp-subtask-tree-item"
        style={{ marginLeft: `${level * 20}px` }}
      >
        <div className="ntp-subtask-tree-content">
          <div className="ntp-subtask-tree-info">
            <strong>{subTask.subTaskName}</strong>
            {subTask.subTaskDescription && (
              <span className="ntp-subtask-tree-desc">
                {subTask.subTaskDescription}
              </span>
            )}
          </div>
          <div className="ntp-subtask-tree-actions">
            <span
              className={`ntp-subtask-status ${getStatusClass(subTask.subTaskStatus)}`}
            >
              {getTaskStatusName(subTask.subTaskStatus)}
            </span>
            {canAddSubTask() && (
              <>
                <button
                  onClick={() => handleAddSubTask(subTask.subTaskId)}
                  className="ntp-icon-btn-small"
                >
                  + Child
                </button>
                <button
                  onClick={() => handleEditSubTask(subTask)}
                  className="ntp-icon-btn-small"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteSubTask(subTask.subTaskId)}
                  className="ntp-icon-btn-small ntp-danger"
                >
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
        {subTask.children && renderSubTasks(subTask.children, level + 1)}
      </div>
    ));
  };

  useEffect(() => {
    if (!taskData && !taskAssignments.length) return;

    const guidSet = new Set();

    // Lead Owner
    if (taskData?.assignedTo) {
      guidSet.add(taskData.assignedTo);
    }

    // Assignment Logs
    taskAssignments.forEach((a) => {
      if (a.assignedUserId) {
        guidSet.add(a.assignedUserId);
      }
    });

    fetchContactNames(Array.from(guidSet));
  }, [taskData, taskAssignments, fetchContactNames]);

  if (loading) {
    return (
      <div className="ntp-loader-container">
        <div className="ntp-spinner"></div>
        <p className="ntp-loading-text">Loading task details...</p>
      </div>
    );
  }

  if (!taskData) {
    return (
      <div className="ntp-error-container">
        <span className="material-symbols-outlined ntp-error-icon">error</span>
        <p className="ntp-error-text">Failed to load task details</p>
        <button className="ntp-btn-primary" onClick={handleBack}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="ntp-container">
      {/* Header */}
      <header className="ntp-header">
        <div className="ntp-header-content">
          <div className="ntp-header-left">
            <button className="ntp-icon-btn" onClick={handleBack}>
              <span className="material-symbols-outlined">
                <IoArrowBackCircle />
              </span>
            </button>
            <div className="ntp-header-info">
              <h1 className="ntp-title">Task Details</h1>
              <span className="ntp-task-id-badge">#{taskData.taskId}</span>
            </div>
          </div>
          <div className="ntp-header-actions">
            {isEditing && canEditTask() && (
              <button className="ntp-btn-secondary" onClick={handleDiscard}>
                Discard
              </button>
            )}
            {canEditTask() && (
              <button
                className="ntp-btn-primary"
                onClick={isEditing ? handleSubmit : () => setIsEditing(true)}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : isEditing
                    ? "Save Changes"
                    : "Edit Task"}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="ntp-main">
        <form onSubmit={handleSubmit} className="ntp-form">
          {/* Task Header Section - Always editable for admins/managers */}
          <section className="ntp-section-card">
            <div className="ntp-section-content">
              <div className="ntp-field-group">
                <label className="ntp-label-primary">Primary Task Title</label>
                {isEditing && canEditTask() ? (
                  <input
                    type="text"
                    name="taskName"
                    value={taskData.taskName || ""}
                    onChange={handleInputChange}
                    className="ntp-input-title"
                    placeholder="Enter task name..."
                    required
                  />
                ) : (
                  <div className="ntp-field-value">{taskData.taskName}</div>
                )}
              </div>
              <div className="ntp-field-group">
                <label className="ntp-label-secondary">
                  Contextual Description
                </label>
                {isEditing && canEditTask() ? (
                  <textarea
                    name="taskDescription"
                    value={taskData.taskDescription || ""}
                    onChange={handleInputChange}
                    className="ntp-textarea"
                    placeholder="Describe the task details..."
                    rows={2}
                  />
                ) : (
                  <div className="ntp-field-value">
                    {taskData.taskDescription || "No description"}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Two Column Layout */}
          <div className="ntp-two-columns">
            {/* Technical Specs */}
            <section className="ntp-section-card">
              <div className="ntp-section-header">
                <div className="ntp-section-icon ntp-icon-blue">
                  <span className="material-symbols-outlined">
                    <RiCompassesFill />
                  </span>
                </div>
                <h3 className="ntp-section-title">Technical Specifications</h3>
              </div>
              <div className="ntp-grid-3cols">
                <div className="ntp-field-group">
                  <label>Task Category</label>
                  {isEditing && canEditTask() ? (
                    <select
                      name="taskType"
                      value={taskData.taskType || ""}
                      onChange={handleInputChange}
                      className="ntp-select"
                      required
                    >
                      <option value="">
                        {loadingTaskTypes ? "Loading..." : "Select Task Type"}
                      </option>
                      {taskTypes.map((type) => (
                        <option key={type.typeGuid} value={type.typeGuid}>
                          {type.typeName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="ntp-field-value">
                      {getTaskTypeName(taskData.taskType)}
                    </div>
                  )}
                </div>
                <div className="ntp-field-group">
                  <label>Workflow Status</label>
                  {isEditing && canEditTask() ? (
                    <select
                      name="taskStatus"
                      value={taskData.taskStatus || ""}
                      onChange={handleInputChange}
                      className={`ntp-select ${getStatusClass(taskData.taskStatus || "")}`}
                      required
                    >
                      <option value="">
                        {loadingStatus ? "Loading..." : "Select Status"}
                      </option>
                      {statusList.map((status) => (
                        <option
                          key={status.statusGuid}
                          value={status.statusGuid}
                        >
                          {status.statusName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="ntp-field-value">
                      <span
                        className={`ntp-status-badge ${getStatusClass(taskData.taskStatus || "")}`}
                      >
                        {getTaskStatusName(taskData.taskStatus)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ntp-field-group">
                  <label>Urgency Level</label>
                  {isEditing && canEditTask() ? (
                    <select
                      name="taskPriority"
                      value={taskData.taskPriority || ""}
                      onChange={handleInputChange}
                      className={`ntp-select ${getPriorityClass(taskData.taskPriority || "")}`}
                      required
                    >
                      <option value="">Select Priority</option>
                      <option value="Low">Low Priority</option>
                      <option value="Medium">Medium Priority</option>
                      <option value="High">High Priority</option>
                      <option value="Critical">Critical Priority</option>
                    </select>
                  ) : (
                    <div className="ntp-field-value">
                      <span
                        className={`ntp-priority-badge ${getPriorityClass(taskData.taskPriority || "")}`}
                      >
                        {taskData.taskPriority || "No Priority"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ntp-field-group">
                  <label>Project Phase</label>
                  {isEditing && canEditTask() ? (
                    loadingStages ? (
                      <div className="ntp-loading-placeholder">
                        Loading stages...
                      </div>
                    ) : (
                      <select
                        name="stageBelong"
                        value={taskData.stageBelong || ""}
                        onChange={handleStageChange}
                        className="ntp-select"
                      >
                        <option value="">No Stage</option>
                        {stages.map((stage) => (
                          <option key={stage.stageId} value={stage.stageId}>
                            {stage.stageName}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <div className="ntp-field-value">
                      {getStageName(taskData.stageBelong)}
                    </div>
                  )}
                </div>
                <div className="ntp-field-group">
                  <label>Lead Owner</label>
                  <div className="ntp-assignee-preview">
                    <div className="ntp-avatar-placeholder">L</div>
                    <span className="ntp-assignee-name">
                      {getUsernameFromId(taskData.assignedTo) || "Unassigned"}
                    </span>
                  </div>
                </div>
                <div className="ntp-field-group">
                  <label>Unique ID</label>
                  <input
                    type="text"
                    value={taskData.taskId}
                    className="ntp-input-readonly ntp-mono"
                    readOnly
                  />
                </div>
              </div>
            </section>

            {/* Timeline Section */}
            <section className="ntp-section-card">
              <div className="ntp-section-header">
                <div className="ntp-section-icon ntp-icon-blue">
                  <span className="material-symbols-outlined">
                    <FaCalendarCheck />{" "}
                  </span>
                </div>
                <h3 className="ntp-section-title">Timeline &amp; Milestones</h3>
              </div>
              <div className="ntp-grid-4cols">
                <div className="ntp-timeline-item">
                  <label className="ntp-timeline-label">Planned Start</label>
                  {isEditing && canEditTask() ? (
                    <input
                      type="date"
                      name="taskStartDate"
                      value={formatDateForInput(taskData.taskStartDate)}
                      onChange={handleDateChange}
                      className="ntp-date-input"
                      required
                    />
                  ) : (
                    <div className="ntp-field-value">
                      {formatDateForDisplay(taskData.taskStartDate)}
                    </div>
                  )}
                </div>
                <div className="ntp-timeline-item">
                  <label className="ntp-timeline-label">Deadline</label>
                  {isEditing && canEditTask() ? (
                    <input
                      type="date"
                      name="taskEndDate"
                      value={formatDateForInput(taskData.taskEndDate)}
                      onChange={handleDateChange}
                      className="ntp-date-input"
                      required
                    />
                  ) : (
                    <div className="ntp-field-value">
                      {formatDateForDisplay(taskData.taskEndDate)}
                    </div>
                  )}
                </div>
                <div className="ntp-timeline-item">
                  <label className="ntp-timeline-label">Actual Start</label>
                  {isEditing && canEditTask() ? (
                    <input
                      type="date"
                      name="actualStartDate"
                      value={formatDateForInput(taskData.actualStartDate)}
                      onChange={handleDateChange}
                      className="ntp-date-input"
                    />
                  ) : (
                    <div className="ntp-field-value">
                      {formatDateForDisplay(taskData.actualStartDate)}
                    </div>
                  )}
                </div>
                <div className="ntp-timeline-item">
                  <label className="ntp-timeline-label">Actual End</label>
                  {isEditing && canEditTask() ? (
                    <input
                      type="date"
                      name="actualEndDate"
                      value={formatDateForInput(taskData.actualEndDate)}
                      onChange={handleDateChange}
                      className="ntp-date-input"
                    />
                  ) : (
                    <div className="ntp-field-value">
                      {formatDateForDisplay(taskData.actualEndDate)}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>

          {/* Three Column Layout - Blocks, Activity, Sub-tasks */}
          <div className="ntp-three-columns">
            {/* Building Blocks */}
            {/* Building Blocks */}
            {/* Building Blocks */}
            <section className="ntp-compact-card">
              <div className="ntp-compact-header">
                <h4 className="ntp-compact-title">Building Blocks</h4>
              </div>

              {/* Show selected blocks below title when NOT editing */}
              {(!isEditing || !canEditTask()) &&
                selectedBlockIds.length > 0 && (
                  <div className="ntp-building-blocks-assigned">
                    {/* <label className="ntp-building-blocks-label">
                      Assigned Blocks:
                    </label> */}
                    <div className="ntp-tags-container">
                      {selectedBlockIds.map((blockId) => {
                        const block = buildingBlocks.find(
                          (b) => b.buildingBlockId === blockId,
                        );
                        return (
                          <span key={blockId} className="ntp-tag">
                            <p>{block?.buildingBlockName || blockId}</p>
                            {/* <p> {block?.buildingBlockId || "ID not found"}</p> */}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Show selection/dropdown when editing */}
              {isEditing && canEditTask() && (
                <div className="ntp-building-blocks-editor">
                  <label className="ntp-building-blocks-label">
                    Select/Edit Blocks:
                  </label>
                  {buildingBlockOptions.length > 0 ? (
                    <Select
                      isMulti
                      options={buildingBlockOptions}
                      value={buildingBlockOptions.filter((opt) =>
                        selectedBlockIds.includes(opt.value),
                      )}
                      onChange={handleBuildingBlockChange}
                      classNamePrefix="ntp-react-select"
                      placeholder="Select blocks..."
                      isLoading={loadingBlocks}
                    />
                  ) : (
                    <div className="ntp-empty-state-small">
                      <p className="ntp-empty-text">
                        No building blocks available
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Show message when no blocks assigned in view mode */}
              {(!isEditing || !canEditTask()) &&
                selectedBlockIds.length === 0 && (
                  <div className="ntp-building-blocks-assigned">
                    <label className="ntp-building-blocks-label">
                      Assigned Blocks:
                    </label>
                    <span className="ntp-tag-placeholder">
                      No building blocks assigned
                    </span>
                  </div>
                )}

              <div className="ntp-compact-footer">
                <h4 className="ntp-compact-title ntp-mt-2">Tags</h4>
                {isEditing && canEditTask() ? (
                  <input
                    type="text"
                    name="taskTags"
                    value={taskData.taskTags || ""}
                    onChange={handleTagsChange}
                    className="ntp-input-small"
                    placeholder="Comma separated tags"
                  />
                ) : (
                  <div className="ntp-tags-container">
                    {taskData.taskTags ? (
                      taskData.taskTags.split(",").map((tag, i) => (
                        <span key={i} className="ntp-tag">
                          {tag.trim()}
                        </span>
                      ))
                    ) : (
                      <span className="ntp-tag-placeholder">No tags</span>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Activity Feed */}
            <section className="ntp-compact-card">
              <div className="ntp-compact-header">
                <h4 className="ntp-compact-title">
                  <span className="material-symbols-outlined ntp-icon-primary">
                    <MdOutlineChatBubble />
                  </span>
                  Activity Feed{" "}
                  <span className="ntp-badge">{remarks.length}</span>
                </h4>
              </div>
              {canAddComment() && (
                <div className="ntp-comment-input">
                  <textarea
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    className="ntp-comment-textarea"
                    placeholder="Add a comment..."
                    rows={2}
                  />
                  <button
                    type="button"
                    className="ntp-btn-gradient"
                    onClick={handleAddRemark}
                  >
                    Post
                  </button>
                </div>
              )}
              <div className="ntp-activity-feed">
                {loadingRemarks ? (
                  <div className="ntp-empty-state">
                    <div className="ntp-spinner-small"></div>
                  </div>
                ) : remarks.length === 0 ? (
                  <div className="ntp-empty-state">
                    <span className="material-symbols-outlined">history</span>
                    <p className="ntp-empty-text">No activity yet</p>
                  </div>
                ) : (
                  remarks.map((remark) => (
                    <div key={remark.taskLogId} className="ntp-activity-item">
                      <div className="ntp-activity-avatar">
                        {remark.createdBy?.charAt(0) || "U"}
                      </div>
                      <div className="ntp-activity-content">
                        <p className="ntp-activity-text">
                          {remark.logDescription}
                        </p>
                        <span className="ntp-activity-time">
                          {moment(remark.createdDate).format("MMM DD, YYYY")}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Sub-Tasks */}
            {/* Sub-Tasks - Redesigned Tree Structure */}
            <section className="ntp-compact-card ntp-subtasks-card">
              <div className="ntp-compact-header">
                <h4 className="ntp-compact-title">
                  <span className="material-symbols-outlined ntp-icon-tertiary">
                    <MdAccountTree />
                  </span>
                  Sub-Tasks
                  {subTasks.length > 0 && (
                    <span className="ntp-subtask-count">{subTasks.length}</span>
                  )}
                </h4>
                {canAddSubTask() && (
                  <button
                    type="button"
                    className="ntp-add-subtask-btn"
                    onClick={() => handleAddSubTask(null)}
                  >
                    <IoMdAddCircle className="ntp-add-icon" />
                    <span>Add Sub-task</span>
                  </button>
                )}
              </div>

              <div className="ntp-subtasks-tree-container">
                {subTasks.length === 0 ? (
                  <div className="ntp-subtasks-empty">
                    <div className="ntp-empty-icon-wrapper">
                      <MdAccountTree className="ntp-empty-tree-icon" />
                    </div>
                    <p className="ntp-empty-text">No sub-tasks created yet</p>
                    {canAddSubTask() && (
                      <button
                        type="button"
                        className="ntp-btn-outline-primary ntp-create-first-btn"
                        onClick={() => handleAddSubTask(null)}
                      >
                        + Create First Sub-task
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="ntp-subtasks-tree-view">
                    {renderSubTasksTree(subTasks)}
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Assignment Section */}
          {/* Assignment Section */}
          <section className="ntp-section-card">
            <div className="ntp-section-header">
              <div className="ntp-section-icon ntp-icon-primary-bg">
                <span className="material-symbols-outlined">
                  <MdPersonAddAlt1 />
                </span>
              </div>
              <h3 className="ntp-section-title">Manage Task Assignment</h3>
            </div>

            <div className="ntp-assignment-form">
              <label>Assign To</label>
              <div className="ntp-assignee-select-group">
                {loadingAssignedTo ? (
                  <div className="ntp-loading-placeholder">
                    Loading assignees...
                  </div>
                ) : (
                  <select
                    value={assignTaskUserId}
                    onChange={(e) => setAssignTaskUserId(e.target.value)}
                    className="ntp-select-flex"
                    disabled={!canEditTask() || assignedToOptions.length === 0}
                  >
                    <option value="">
                      {assignedToOptions.length === 0
                        ? "No assignees available"
                        : "Select Assignee"}
                    </option>
                    {getAllAssigneeOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
                {canEditTask() && (
                  <button
                    type="button"
                    className="ntp-btn-assign"
                    onClick={handleAssignUserOnly}
                    disabled={!assignTaskUserId}
                  >
                    ASSIGN NOW
                  </button>
                )}
              </div>
            </div>

            <div className="ntp-assignment-logs">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <label style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  Assignment Logs
                </label>
                <button
                  type="button"
                  onClick={toggleDeletedAssignments}
                  className="ntp-link-btn"
                  style={{
                    fontSize: "12px",
                    padding: "4px 8px",
                    background: showDeletedAssignments ? "#dc2626" : "#6b7280",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {showDeletedAssignments ? "Show Active" : "Show Deleted"}
                </button>
              </div>

              <div className="ntp-table-container">
                <div className="ntp-table-wrapper-scroll ntp-assignment-table-container">
                  <table className="ntp-table-fixed-header">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Hours</th>
                        <th>Status</th>
                        <th>Current</th>
                        <th className="ntp-text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskAssignLoading || loadingDeleted ? (
                        <tr>
                          <td colSpan="7" className="ntp-table-empty">
                            Loading...
                          </td>
                        </tr>
                      ) : showDeletedAssignments ? (
                        // ✅ Show deleted assignments
                        deletedAssignments.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="ntp-table-empty">
                              No deleted assignments found
                            </td>
                          </tr>
                        ) : (
                          deletedAssignments.map((assign) => {
                            const userName = getUsernameFromId(
                              assign.assignedUserId,
                            );
                            return (
                              <tr
                                key={assign.assignmentId}
                                style={{
                                  opacity: 0.7,
                                  backgroundColor: "#fef2f2",
                                }}
                              >
                                <td className="ntp-font-bold">{userName}</td>
                                <td>{formatDateTime(assign.startTime)}</td>
                                <td>
                                  {formatDateTime(assign.endTime) || "Ongoing"}
                                </td>
                                <td className="ntp-font-mono ntp-text-primary">
                                  {fmtHours(assign.totalHours)}
                                </td>
                                <td>
                                  <span className="ntp-status-badge status-deleted">
                                    Deleted
                                  </span>
                                  {assign.deletedDate && (
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#999",
                                        marginTop: "4px",
                                      }}
                                    >
                                      Deleted:{" "}
                                      {moment(assign.deletedDate).format(
                                        "MMM DD, YYYY",
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="ntp-text-center">
                                  <input
                                    type="checkbox"
                                    checked={
                                      assign.isCurrentAssignment === true
                                    }
                                    disabled={true}
                                  />
                                </td>
                                <td className="ntp-text-right">
                                  {canEditTask() && (
                                    <button
                                      type="button"
                                      className="ntp-btn-restore"
                                      onClick={() =>
                                        handleRestoreAssignment(assign)
                                      }
                                      style={{
                                        padding: "4px 8px",
                                        fontSize: "12px",
                                        background: "#10b981",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Restore
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        )
                      ) : // ✅ Show active assignments
                      taskAssignments.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="ntp-table-empty">
                            No assignment logs
                          </td>
                        </tr>
                      ) : (
                        taskAssignments.map((assign) => {
                          const userName = getUsernameFromId(
                            assign.assignedUserId,
                          );
                          const isCurrentUser =
                            String(assign.assignedUserId) === String(userId);
                          const isNotStartedStatus =
                            !assign.startTime && !assign.endTime;

                          return (
                            <tr key={assign.assignmentId}>
                              <td className="ntp-font-bold">{userName}</td>
                              <td>{formatDateTime(assign.startTime)}</td>
                              <td>
                                {formatDateTime(assign.endTime) || "Ongoing"}
                              </td>
                              <td className="ntp-font-mono ntp-text-primary">
                                {fmtHours(assign.totalHours)}
                              </td>
                              <td>
                                <span
                                  className={`ntp-status-badge ${getAssignmentStatusClass(assign)}`}
                                >
                                  {assign.isPaused
                                    ? "Paused"
                                    : assign.isActive
                                      ? "Active"
                                      : !assign.startTime
                                        ? "Not Started"
                                        : "Completed"}
                                </span>
                              </td>
                              <td className="ntp-text-center">
                                <input
                                  type="checkbox"
                                  style={{ margin: "10px" }}
                                  checked={assign.isCurrentAssignment === true}
                                  disabled={!canEditTask()}
                                  onChange={() =>
                                    handleToggleCurrentAssignment(
                                      assign.assignmentId,
                                      assign.isCurrentAssignment,
                                    )
                                  }
                                />
                              </td>
                              <td className="ntp-text-right">
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    justifyContent: "center",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  {/* Show Start button only for Not Started assignments */}
                                  {isNotStartedStatus &&
                                    (canEditTask() || isCurrentUser) && (
                                      <button
                                        type="button"
                                        className="ntp-btn-start"
                                        onClick={() =>
                                          handleStartTaskWork(
                                            assign.assignedUserId,
                                          )
                                        }
                                        style={{
                                          padding: "4px 8px",
                                          fontSize: "11px",
                                        }}
                                      >
                                        Start
                                      </button>
                                    )}

                                  {/* Show Pause/Stop for Active assignments */}
                                  {assign.isActive &&
                                    (canEditTask() || isCurrentUser) && (
                                      <>
                                        <button
                                          type="button"
                                          className="ntp-btn-pause"
                                          onClick={() =>
                                            handlePauseTaskWork(
                                              assign.assignmentId,
                                            )
                                          }
                                          style={{
                                            padding: "4px 8px",
                                            fontSize: "11px",
                                          }}
                                        >
                                          Pause
                                        </button>
                                        <button
                                          type="button"
                                          className="ntp-btn-stop"
                                          onClick={() =>
                                            handleStopTaskWork(
                                              assign.assignmentId,
                                            )
                                          }
                                          style={{
                                            padding: "4px 8px",
                                            fontSize: "11px",
                                          }}
                                        >
                                          Stop
                                        </button>
                                      </>
                                    )}

                                  {/* Show Resume/Stop for Paused assignments */}
                                  {assign.isPaused &&
                                    (canEditTask() || isCurrentUser) && (
                                      <>
                                        <button
                                          type="button"
                                          className="ntp-btn-resume"
                                          onClick={() =>
                                            handleStartTaskWork(
                                              assign.assignedUserId,
                                            )
                                          }
                                          style={{
                                            padding: "4px 8px",
                                            fontSize: "11px",
                                          }}
                                        >
                                          Resume
                                        </button>
                                        <button
                                          type="button"
                                          className="ntp-btn-stop"
                                          onClick={() =>
                                            handleStopTaskWork(
                                              assign.assignmentId,
                                            )
                                          }
                                          style={{
                                            padding: "4px 8px",
                                            fontSize: "11px",
                                          }}
                                        >
                                          Stop
                                        </button>
                                      </>
                                    )}

                                  {/* ✅ Delete button - ONLY SHOW for Not Started assignments */}
                                  {isNotStartedStatus && canEditTask() && (
                                    <button
                                      type="button"
                                      className="ntp-btn-delete"
                                      onClick={() =>
                                        handleSoftDeleteAssignment(assign)
                                      }
                                      style={{
                                        padding: "1px 5px",
                                        fontSize: "21px",
                                        background: "#dc2626",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                      title="Soft Delete Assignment"
                                    >
                                      <MdDeleteOutline />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="ntp-manual-entry">
              <label>Manual Work Log</label>
              <div className="ntp-manual-grid">
                <select
                  className="ntp-select-small"
                  value={workLogUserId}
                  onChange={(e) => setWorkLogUserId(e.target.value)}
                >
                  <option value="">-- User --</option>
                  {getAllAssigneeOptions().map((opt) => (
                    <option
                      key={opt.value}
                      value={opt.value}
                      disabled={
                        !canEditTask() && String(opt.value) !== String(userId)
                      }
                    >
                      {opt.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.25"
                  className="ntp-input-small"
                  placeholder="Hours"
                  value={workValue}
                  onChange={(e) => setWorkValue(e.target.value)}
                />
                <select
                  className="ntp-select-small"
                  value={workUnit}
                  onChange={(e) => setWorkUnit(e.target.value)}
                >
                  <option value="HOUR">Hours</option>
                  <option value="DAY">Days</option>
                </select>
                <button
                  type="button"
                  className="ntp-btn-gradient"
                  onClick={handleSaveWorkLog}
                  disabled={savingWorkLog || !workLogUserId || !workValue}
                >
                  {savingWorkLog ? "Saving..." : "ADD"}
                </button>
              </div>
            </div>

            <div className="ntp-task-logs">
              <div className="ntp-task-logs-header">
                <label>Task Log History</label>

                {/* Filter Dropdown */}
                <div className="ntp-log-filter">
                  <span className="ntp-log-filter-label">Filter by Type:</span>
                  <select
                    value={logTypeFilter}
                    onChange={(e) => setLogTypeFilter(e.target.value)}
                    className="ntp-select-small ntp-filter-select"
                  >
                    {getUniqueLogTypes.map((type) => (
                      <option key={type} value={type}>
                        {type === "ALL" ? "All Types" : type}
                      </option>
                    ))}
                  </select>

                  {/* Clear filter button */}
                  {logTypeFilter !== "ALL" && (
                    <button
                      onClick={() => setLogTypeFilter("ALL")}
                      className="ntp-clear-filter-btn"
                      title="Clear filter"
                    >
                      ✕ Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Show filter results count */}
              {logTypeFilter !== "ALL" && (
                <div className="ntp-filter-results-count">
                  Showing {filteredTaskLogs.length} of {taskLogs.length} entries
                </div>
              )}

              <div className="ntp-table-container">
                <div className="ntp-table-wrapper-scroll ntp-tasklogs-table-container">
                  <table className="ntp-table-fixed-header">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Work</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskLogsLoading ? (
                        <tr>
                          <td colSpan="5" className="ntp-table-empty">
                            Loading...
                          </td>
                        </tr>
                      ) : filteredTaskLogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="ntp-table-empty">
                            {taskLogs.length === 0
                              ? "No task logs found"
                              : `No ${logTypeFilter} logs found`}
                          </td>
                        </tr>
                      ) : (
                        filteredTaskLogs.map((log) => (
                          <tr key={log.taskLogId}>
                            <td>
                              {/* Show name for CreatedBy */}
                              {getUsernameFromId(log.createdBy)}
                            </td>
                            <td>
                              {log.workValue
                                ? `${log.workValue} ${log.workUnit}`
                                : "-"}
                            </td>
                            <td>
                              <span
                                className={`ntp-log-type-badge ntp-log-type-${log.logType?.toLowerCase()}`}
                              >
                                {log.logType}
                              </span>
                              {/* Show closure type if it's a closure log */}
                              {log.logType === "TASK_CLOSURE" &&
                                log.closureType && (
                                  <span
                                    style={{
                                      marginLeft: "8px",
                                      fontSize: "10px",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background:
                                        log.closureType === "Completed"
                                          ? "#10b981"
                                          : "#dc2626",
                                      color: "white",
                                    }}
                                  >
                                    {log.closureType}
                                  </span>
                                )}
                            </td>
                            <td>
                              {/* Show log description */}
                              {log.logDescription || "-"}
                              {/* Show closure reason if available */}
                              {log.logType === "TASK_CLOSURE" &&
                                log.closureReason && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#666",
                                      marginTop: "4px",
                                    }}
                                  >
                                    Reason: {log.closureReason}
                                  </div>
                                )}
                            </td>
                            <td>
                              {moment(log.createdDate).format("MMM DD, YYYY")}
                              {log.createdTime && (
                                <div
                                  style={{ fontSize: "10px", color: "#999" }}
                                >
                                  {moment(log.createdTime, "HH:mm:ss").format(
                                    "hh:mm A",
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </form>
      </main>

      {/* Unified SubTask Modal - Add/Edit */}
      {(showSubTaskModal || showEditSubTaskModal) && (
        <div
          className="ntp-modal-overlay"
          onClick={() => {
            setShowSubTaskModal(false);
            setShowEditSubTaskModal(false);
            setEditingSubTask(null);
          }}
        >
          <div
            className="ntp-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px" }}
          >
            <h3 style={{ marginBottom: "16px" }}>
              {editingSubTask
                ? "Edit Sub Task"
                : parentSubTaskId
                  ? "Add Child Sub Task"
                  : "Add New Sub Task"}
            </h3>

            <input
              type="text"
              placeholder="SubTask Name *"
              value={subTaskForm.SubTaskName}
              onChange={(e) =>
                handleSubTaskFormChange("SubTaskName", e.target.value)
              }
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            />

            <textarea
              placeholder="Description"
              rows={3}
              value={subTaskForm.SubTaskDescription}
              onChange={(e) =>
                handleSubTaskFormChange("SubTaskDescription", e.target.value)
              }
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                fontFamily: "inherit",
              }}
            />

            <select
              value={subTaskForm.SubTaskAssignedTo}
              onChange={(e) =>
                handleSubTaskFormChange("SubTaskAssignedTo", e.target.value)
              }
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="">Select Assignee</option>
              {getAllAssigneeOptions().map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={subTaskForm.SubTaskStatus}
              onChange={(e) =>
                handleSubTaskFormChange("SubTaskStatus", e.target.value)
              }
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            >
              <option value="">Select Status</option>
              {statusList.map((status) => (
                <option key={status.statusGuid} value={status.statusGuid}>
                  {status.statusName}
                </option>
              ))}
            </select>

            {/* Optional: Add date fields for subtasks */}
            <input
              type="date"
              placeholder="Due Date"
              value={subTaskForm.SubTaskDueDate}
              onChange={(e) =>
                handleSubTaskFormChange("SubTaskDueDate", e.target.value)
              }
              style={{
                width: "100%",
                padding: "8px",
                marginBottom: "12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
              }}
            />

            <div
              className="ntp-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => {
                  setShowSubTaskModal(false);
                  setShowEditSubTaskModal(false);
                  setEditingSubTask(null);
                }}
                style={{
                  padding: "8px 16px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={
                  editingSubTask ? handleUpdateSubTask : handleSubTaskSubmit
                }
                disabled={subTaskLoading || !subTaskForm.SubTaskName.trim()}
                style={{
                  padding: "8px 16px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  opacity:
                    subTaskLoading || !subTaskForm.SubTaskName.trim() ? 0.6 : 1,
                }}
              >
                {subTaskLoading
                  ? editingSubTask
                    ? "Updating..."
                    : "Creating..."
                  : editingSubTask
                    ? "Update"
                    : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAssignment && (
        <div
          className="ntp-modal-overlay"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="ntp-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "400px" }}
          >
            <h3 style={{ color: "#dc2626", marginBottom: "16px" }}>
              Soft Delete Assignment
            </h3>
            <p style={{ marginBottom: "16px" }}>
              Are you sure you want to delete the assignment for{" "}
              <strong>
                {getUsernameFromId(selectedAssignment.assignedUserId)}
              </strong>
              ?
            </p>
            <p
              style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}
            >
              This action will mark the assignment as deleted. You can restore
              it later if needed.
            </p>

            <div className="ntp-field-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                Reason for deletion (optional):
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Enter reason for deleting this assignment..."
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
            </div>

            <div
              className="ntp-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  padding: "8px 16px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSoftDelete}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Closure Modal */}
      {showClosureModal && (
        <div
          className="ntp-modal-overlay"
          onClick={() => setShowClosureModal(false)}
        >
          <div
            className="ntp-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px" }}
          >
            <h3
              style={{
                color: closureType === "Completed" ? "#10b981" : "#dc2626",
                marginBottom: "16px",
              }}
            >
              {closureType === "Completed" ? "Complete Task" : "Cancel Task"}
            </h3>

            <p
              style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}
            >
              You are about to mark this task as <strong>{closureType}</strong>.
              This will create a closure record and the task will be considered
              closed.
            </p>

            <div className="ntp-field-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                Closure Reason <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <textarea
                value={closureReason}
                onChange={(e) => setClosureReason(e.target.value)}
                placeholder={`Why is this task being ${closureType === "Completed" ? "completed" : "cancelled"}?`}
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div className="ntp-field-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                Additional Notes (optional)
              </label>
              <textarea
                value={closureNotes}
                onChange={(e) => setClosureNotes(e.target.value)}
                placeholder="Any additional information about the closure..."
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              className="ntp-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowClosureModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCloseTask}
                disabled={isClosingTask || !closureReason.trim()}
                style={{
                  padding: "8px 16px",
                  background:
                    closureType === "Completed" ? "#10b981" : "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  opacity: isClosingTask || !closureReason.trim() ? 0.6 : 1,
                }}
              >
                {isClosingTask ? "Closing..." : `Confirm ${closureType}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Modal */}
      {showReopenModal && (
        <div
          className="ntp-modal-overlay"
          onClick={() => setShowReopenModal(false)}
        >
          <div
            className="ntp-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "500px" }}
          >
            <h3 style={{ color: "#3b82f6", marginBottom: "16px" }}>
              Reopen Task
            </h3>

            <p
              style={{ marginBottom: "16px", fontSize: "14px", color: "#666" }}
            >
              This task is currently closed. Reopening it will allow work to
              continue.
            </p>

            <div className="ntp-field-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                Reopen Reason <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Why is this task being reopened?"
                rows={3}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div className="ntp-field-group" style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: 500,
                }}
              >
                Additional Notes (optional)
              </label>
              <textarea
                value={reopenNotes}
                onChange={(e) => setReopenNotes(e.target.value)}
                placeholder="Any additional information about the reopening..."
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              className="ntp-modal-actions"
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowReopenModal(false)}
                style={{
                  padding: "8px 16px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReopenTask}
                disabled={isReopeningTask || !reopenReason.trim()}
                style={{
                  padding: "8px 16px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  opacity: isReopeningTask || !reopenReason.trim() ? 0.6 : 1,
                }}
              >
                {isReopeningTask ? "Reopening..." : "Confirm Reopen"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Show a badge if task is closed */}
      {taskClosedStatus.isClosed && !isEditing && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#dc2626",
            color: "white",
            padding: "8px 16px",
            borderRadius: "20px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 1000,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          🔒 Task Closed
        </div>
      )}

      {/* Add CSS for deleted status */}
      <style jsx>{`
        .status-deleted {
          background-color: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }
        .ntp-btn-restore:hover {
          background-color: #059669 !important;
        }
        .ntp-btn-delete:hover {
          background-color: #b91c1c !important;
        }
        .ntp-link-btn:hover {
          opacity: 0.8;
        }
      `}</style>

      {/* FAB for mobile */}
      <button className="ntp-fab">
        <span className="material-symbols-outlined">add</span>
      </button>
    </div>
  );
};

export default TaskDetailsPage;
